import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, MapPin, Calendar, Check, AlertCircle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
type PhotoComparison = {
  roomName: string;
  beforePhoto: string;
  afterPhoto: string;
};
type HostInstruction = {
  id: string;
  text: string;
  completed: boolean;
};
type ReportedIssue = {
  id: string;
  title: string;
  severity: 'low' | 'medium' | 'high';
};
export function SummaryPage() {
  const {
    t
  } = useLanguage();
  const navigate = useNavigate();
  // Mock data
  const duration = '1h 23min';
  const address = 'Via Roma 123, Appartamento 4B';
  const date = '15 Gennaio 2025';
  const time = '10:00 - 11:23';
  const hostInstructions: HostInstruction[] = [{
    id: '1',
    text: 'Utilizzare prodotti eco-friendly',
    completed: true
  }, {
    id: '2',
    text: 'Prestare attenzione ai mobili antichi',
    completed: true
  }, {
    id: '3',
    text: 'Arieggiare tutti gli ambienti',
    completed: true
  }, {
    id: '4',
    text: 'Controllare scorte bagno',
    completed: true
  }];
  const photoComparisons: PhotoComparison[] = [{
    roomName: t.fornitore.beforePhotos.rooms.soggiorno,
    beforePhoto: 'https://images.unsplash.com/photo-1567767292278-a4f21aa2d36e?auto=format&fit=crop&w=400&q=80',
    afterPhoto: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=400&q=80'
  }, {
    roomName: t.fornitore.beforePhotos.rooms.cucina,
    beforePhoto: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=400&q=80',
    afterPhoto: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=400&q=80'
  }, {
    roomName: t.fornitore.beforePhotos.rooms.bagno,
    beforePhoto: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=400&q=80',
    afterPhoto: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=400&q=80'
  }, {
    roomName: t.fornitore.beforePhotos.rooms.camera1,
    beforePhoto: 'https://images.unsplash.com/photo-1616594039964-40891a909d99?auto=format&fit=crop&w=400&q=80',
    afterPhoto: 'https://images.unsplash.com/photo-1616137466211-f939a420be84?auto=format&fit=crop&w=400&q=80'
  }];
  const reportedIssues: ReportedIssue[] = [{
    id: '1',
    title: 'Macchia persistente sul divano',
    severity: 'medium'
  }];
  const handleSubmit = () => {
    // Submit and navigate to completion
    console.log('Submitting intervention summary...');
    navigate('/fornitore');
  };
  const severityColors = {
    low: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    medium: 'bg-orange-50 text-orange-700 border-orange-200',
    high: 'bg-red-50 text-red-700 border-red-200'
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-839">
      <Header data-id="element-840" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32" data-id="element-841">
        {/* Page Header */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-8 flex items-center gap-4" data-id="element-842">
          <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center" data-id="element-843">
            <CheckCircle2 className="w-7 h-7 text-teal-deep" data-id="element-844" />
          </div>
          <div data-id="element-845">
            <h1 className="text-3xl font-bold text-navy mb-2" data-id="element-846">
              {t.fornitore.summary.title}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-847">
              Intervento completato con successo
            </p>
          </div>
        </motion.div>

        {/* Summary Cards Grid */}
        <div className="grid md:grid-cols-2 gap-4 mb-8" data-id="element-848">
          {/* Duration Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-849">
            <Card className="p-6 bg-white border border-gray-200" data-id="element-850">
              <div className="flex items-center gap-4" data-id="element-851">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center" data-id="element-852">
                  <Clock className="w-6 h-6 text-blue-600" data-id="element-853" />
                </div>
                <div data-id="element-854">
                  <p className="text-sm text-gray-500 font-medium" data-id="element-855">
                    {t.fornitore.summary.duration}
                  </p>
                  <p className="text-2xl font-bold text-navy" data-id="element-856">{duration}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Address & Time Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} data-id="element-857">
            <Card className="p-6 bg-white border border-gray-200" data-id="element-858">
              <div className="space-y-3" data-id="element-859">
                <div className="flex items-start gap-3" data-id="element-860">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-861" />
                  <div data-id="element-862">
                    <p className="text-sm text-gray-500" data-id="element-863">
                      {t.fornitore.summary.addressCard.address}
                    </p>
                    <p className="font-semibold text-navy" data-id="element-864">{address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-865">
                  <Calendar className="w-5 h-5 text-gray-400" data-id="element-866" />
                  <div data-id="element-867">
                    <p className="text-sm text-gray-500" data-id="element-868">
                      {t.fornitore.summary.addressCard.date}
                    </p>
                    <p className="font-semibold text-navy" data-id="element-869">
                      {date} • {time}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Host Instructions Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mb-8" data-id="element-870">
          <h2 className="text-xl font-bold text-navy mb-4" data-id="element-871">
            {t.fornitore.summary.hostInstructions.title}
          </h2>
          <Card className="p-6 bg-white border border-gray-200" data-id="element-872">
            <div className="space-y-3" data-id="element-873">
              {hostInstructions.map((instruction, index) => <motion.div key={instruction.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.4 + index * 0.1
            }} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-874">
                  <div className="w-6 h-6 rounded-full bg-teal-deep flex items-center justify-center flex-shrink-0" data-id="element-875">
                    <Check className="w-4 h-4 text-white" data-id="element-876" />
                  </div>
                  <p className="text-gray-700" data-id="element-877">{instruction.text}</p>
                </motion.div>)}
            </div>
          </Card>
        </motion.div>

        {/* Photo Comparison Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="mb-8" data-id="element-878">
          <h2 className="text-xl font-bold text-navy mb-4" data-id="element-879">
            {t.fornitore.summary.photoComparison.title}
          </h2>

          {/* Horizontal Scrollable Container */}
          <div className="relative -mx-4 px-4" data-id="element-880">
            <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" data-id="element-881">
              {photoComparisons.map((comparison, index) => <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.6 + index * 0.1
            }} className="flex-shrink-0 w-[85vw] md:w-[400px] snap-center" data-id="element-882">
                  <Card className="p-4 bg-white border border-gray-200" data-id="element-883">
                    <p className="font-semibold text-navy mb-3" data-id="element-884">
                      {comparison.roomName}
                    </p>
                    <div className="grid grid-cols-2 gap-3" data-id="element-885">
                      {/* Before Photo */}
                      <div data-id="element-886">
                        <p className="text-xs text-gray-500 uppercase font-medium mb-2" data-id="element-887">
                          {t.fornitore.summary.photoComparison.before}
                        </p>
                        <div className="aspect-[4/3] rounded-lg overflow-hidden border border-gray-200" data-id="element-888">
                          <img src={comparison.beforePhoto} alt="Before" className="w-full h-full object-cover" data-id="element-889" />
                        </div>
                      </div>
                      {/* After Photo */}
                      <div data-id="element-890">
                        <p className="text-xs text-gray-500 uppercase font-medium mb-2" data-id="element-891">
                          {t.fornitore.summary.photoComparison.after}
                        </p>
                        <div className="aspect-[4/3] rounded-lg overflow-hidden border-2 border-teal-deep" data-id="element-892">
                          <img src={comparison.afterPhoto} alt="After" className="w-full h-full object-cover" data-id="element-893" />
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>)}
            </div>
          </div>
        </motion.div>

        {/* Reported Issues Section (if any) */}
        {reportedIssues.length > 0 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.7
      }} className="mb-8" data-id="element-894">
            <h2 className="text-xl font-bold text-navy mb-4" data-id="element-895">
              {t.fornitore.summary.reportedIssues.title}
            </h2>
            <div className="space-y-3" data-id="element-896">
              {reportedIssues.map((issue, index) => <motion.div key={issue.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.8 + index * 0.1
          }} data-id="element-897">
                  <Card className={`p-4 border ${severityColors[issue.severity]}`} data-id="element-898">
                    <div className="flex items-center gap-3" data-id="element-899">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" data-id="element-900" />
                      <p className="font-medium" data-id="element-901">{issue.title}</p>
                    </div>
                  </Card>
                </motion.div>)}
            </div>
          </motion.div>}

        {/* Submit Section */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9
      }} className="space-y-4" data-id="element-902">
          <Button onClick={handleSubmit} className="w-full h-14 text-lg font-semibold bg-teal-deep hover:bg-teal-700" data-id="element-903">
            <CheckCircle2 className="w-5 h-5 mr-2" data-id="element-904" />
            {t.fornitore.summary.submitButton}
          </Button>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-905">
            <Sparkles className="w-4 h-4" data-id="element-906" />
            <p data-id="element-907">{t.fornitore.summary.aiNote}</p>
          </div>
        </motion.div>
      </main>
    </div>;
}